// src/components/eframes/EFrame802.tsx
import React from "react";
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  Card,
} from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import { DocumentsIcon } from "../ui/Icons";
import type { AppDocument } from "../../types/document";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface PersonalItem {
  id: string;
  category:
    | "documents"
    | "physical_assets"
    | "collectibles"
    | "electronics"
    | "digital_assets"
    | "custom";
  type?: "account" | "jewelry" | "artwork" | "collectible" | "document" | "other";
  name: string;
  description?: string;
  estimatedValue?: number;
  location?: string;
  recipient?: string;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const EFrame802: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const categories = [
    { key: "documents" as const, label: "Important Documents" },
    { key: "physical_assets" as const, label: "Physical Assets" },
    { key: "collectibles" as const, label: "Collectibles & Valuables" },
    { key: "electronics" as const, label: "Electronics" },
    { key: "digital_assets" as const, label: "Digital Assets" },
    { key: "custom" as const, label: "Other Assets" },
  ];

  const migrateItem = (item: PersonalItem): PersonalItem => {
    const typeToCategory: Record<string, PersonalItem["category"]> = {
      document: "documents",
      jewelry: "collectibles",
      artwork: "collectibles",
      collectible: "collectibles",
      account: "custom",
      other: "custom",
    };

    const category =
      item.category || (item.type ? typeToCategory[item.type] || "custom" : "custom");

    return {
      ...item,
      category,
      documents: normalizeDocuments(item.documents || []),
    };
  };

  const personalItems: PersonalItem[] = (session?.taskProgress?.personal?.answers?.personalItems ?? []).map(migrateItem);

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return "Not specified";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const itemsByCategory = personalItems.reduce((acc, item) => {
    const category = item.category || "custom";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {} as Record<string, PersonalItem[]>);

  const totalValue = personalItems.reduce(
    (sum, item) => sum + (item.estimatedValue || 0),
    0
  );

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <ResponsiveHeader
          title={`${planOwnerName}'s Personal Accounts & Belongings`}
        />

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Personal Items Overview
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">
                  {personalItems.length}
                </div>
                <div className="text-sm text-stone-600">Total Items</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalValue)}
                </div>
                <div className="text-sm text-stone-600">Total Value</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Personal Items by Category
              </h3>
            </div>

            {personalItems.length > 0 ? (
              <div
                className={
                  personalItems.length >= 3
                    ? "max-h-[400px] overflow-y-auto pr-2 custom-scrollbar"
                    : ""
                }
              >
                <div className="space-y-6">
                  {categories.map((category) => {
                    const categoryItems = itemsByCategory[category.key];
                    if (!categoryItems || categoryItems.length === 0) return null;

                    const categoryTotal = categoryItems.reduce(
                      (sum, item) => sum + (item.estimatedValue || 0),
                      0
                    );

                    return (
                      <div key={category.key}>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-lg font-semibold text-stone-800">
                            {category.label}
                          </h3>

                          <span className="text-sm text-stone-500">
                            ({categoryItems.length} items)
                          </span>

                          {categoryTotal > 0 && (
                            <div className="ml-auto text-base font-medium text-green-600">
                              {formatCurrency(categoryTotal)}
                            </div>
                          )}
                        </div>

                        <div className="space-y-3">
                          {categoryItems.map((item, index) => {
                            const attachedDocs = normalizeDocuments(item.documents || []);

                            return (
                              <div
                                key={item.id || index}
                                className="bg-stone-50 rounded-lg p-4"
                              >
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-stone-800 break-words">
                                    {item.name}
                                  </h4>

                                  {item.description && (
                                    <p className="text-sm text-stone-600 mt-1 break-words">
                                      {item.description}
                                    </p>
                                  )}

                                  <div className="flex flex-col gap-1 mt-2 text-sm sm:flex-row sm:flex-wrap sm:gap-4">
                                    {item.estimatedValue !== undefined && (
                                      <span className="text-green-600 font-medium break-words">
                                        Value: {formatCurrency(item.estimatedValue)}
                                      </span>
                                    )}

                                    {item.location && (
                                      <span className="text-stone-500 break-words">
                                        Location: {item.location}
                                      </span>
                                    )}

                                    {item.recipient && (
                                      <span className="text-cyan-600 break-words">
                                        Recipient: {item.recipient}
                                      </span>
                                    )}
                                  </div>

                                  {attachedDocs.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                      {attachedDocs.map((doc, docIndex) => (
                                        <div
                                          key={`${item.id}-${doc.id}-${docIndex}`}
                                          className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
                                        >
                                          <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                          <span
                                            className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                            title={doc.name}
                                          >
                                            {doc.name}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {item.notes && (
                                    <p className="text-sm text-stone-500 mt-2 italic break-words">
                                      {item.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No personal items available.</p>
              </div>
            )}
          </Card>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame802;